// Libraries
import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import { useMsal } from '@azure/msal-react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { Route, Routes } from 'react-router-dom'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'

// Components
import Inbox from '../inbox'
import Error from '../error'
import Profile from '../profile'
import TimeSheet from '../timesheet'
import SchedulesPage from '../schedules'
import SchedulesDriverPage from '../scheduledriver'
import Alerts from '../../components/general/Alerts'
import SchedulesServicePage from '../scheduleservice'
import SchedulesVehiclePage from '../schedulevehicle'
import NavBar from '../../components/v2/general/NavBar'
import SideBar from '../../components/v2/general/SideBar'

// Types and Interfaces
import { RootState } from '../../store/store'
import { Config } from '../../app/configs'

// Utils
import { addAuth, LoginType } from '../../store/slices/function/loginSlice'
import { useGetCompanyTenantsQuery } from '../../store/api/tenants/tenantsApi'
import { buildTenantConfigs } from '../../app/configs'
import { changePca } from '../../store/slices/function/pca'
import { decryptAesGcm } from '../../utils/decrypt'
import { timesheetStore } from '../../store/timesheetStore'

// #region Styles

const homeRootStyle: React.CSSProperties = {
	position: 'relative',
	display: 'flex',
	backgroundColor: 'var(--secondary-light)',
	overflow: 'hidden',
	maxWidth: '100dvw',
	height: '100dvh',
}

const homeMainBoxSx = {
	position: 'relative',
	marginTop: '64px',
	overflow: 'hidden',
	display: 'grid',
	gridTemplateColumns: 'auto 1fr',
	width: '100%',
	padding: '8px',
	gap: '34px',
}

const homeContentBoxSx = {
	top: 0,
	bottom: 0,
	width: '100%',
}

const homeCentralBoxSx = {
	width: '100%',
	height: '100%',
}

const homeGridSx = {
	width: '100%',
	height: '100%',
	padding: '24px 0 0 0',
}

// #endregion

/**
 * A wrapper component that provides the timesheet store to the TimeSheet component.
 * @returns A JSX element representing the TimeSheet component with its own Redux provider.
 */
const TimeSheetWithProvider = () => (
	<Provider store={timesheetStore}>
		<TimeSheet />
	</Provider>
)

/**
 * Home component that serves as the main entry point for the application after login, handling routing and layout for different pages such as Inbox, Profile, and TimeSheet. It also manages authentication with MSAL and dynamic tenant configurations based on data fetched from the API.
 * @returns A JSX element representing the Home component with its layout and routing.
 * @see useMsal for handling authentication with MSAL, which is used within this component to manage user authentication and token acquisition based on dynamic tenant configurations.
 */
const Home = React.memo(function Home() {
	const { instance } = useMsal()
	const dispatch = useDispatch()
	const appPca = useSelector((state: RootState) => state.pcaSlice)
	const [dynamicConfigs, setDynamicConfigs] = useState<Config>({})

	// Fetch tenants from the API
	const { data: tenantsData } = useGetCompanyTenantsQuery()

	/**
	 * Processes tenant data fetched from the API and builds dynamic configurations for MSAL.
	 * This function checks if tenant data is available and contains tenant information, then it processes each tenant by attempting to decrypt certain fields if they exist and are not empty. After processing the tenant data, it builds dynamic configurations using the buildTenantConfigs function and updates the local state with these configurations. This allows the application to dynamically handle multiple tenant configurations based on the data fetched from the API.
	 * @see useGetCompanyTenantsQuery for fetching tenant data from the API, which triggers this function when the data is received or updated.
	 * @see decryptAesGcm for decrypting specific fields in the tenant data, which is used within this function to ensure that sensitive information is properly handled before being used to build configurations.
	 * @see buildTenantConfigs for generating MSAL configurations based on the processed tenant data, which is called within this function to create dynamic configurations that can be used for authentication with multiple tenants.
	 */
	const processTenants = async () => {
		if (tenantsData && tenantsData.length > 0) {
			const processedTenants = await Promise.all(
				tenantsData.map(async tenant => {
					const processedTenant = { ...tenant }

					// Try to decrypt only if the fields exist and are not empty
					if (processedTenant.tenants && processedTenant.tenants.trim() !== '') {
						processedTenant.tenants = await decryptAesGcm(processedTenant.tenants)
					}
					if (processedTenant.audienceId && processedTenant.audienceId.trim() !== '') {
						processedTenant.audienceId = await decryptAesGcm(processedTenant.audienceId)
					}
					return processedTenant
				}),
			)

			const configs = buildTenantConfigs(processedTenants)
			setDynamicConfigs(configs)
		}
	}

	/**
	 * Effect to process tenant data fetched from the API and build dynamic configurations for MSAL.
	 * This effect runs whenever the tenantsData changes, which is the result of the API call to fetch tenant information. It checks if the data is available and contains tenant information, then it processes each tenant by attempting to decrypt certain fields if they exist and are not empty. After processing the tenant data, it builds dynamic configurations using the buildTenantConfigs function and updates the local state with these configurations. This allows the application to dynamically handle multiple tenant configurations based on the data fetched from the API.
	 * @see useGetCompanyTenantsQuery for fetching tenant data from the API, which triggers this effect when the data is received or updated.
	 * @see decryptAesGcm for decrypting specific fields in the tenant data, which is used within this effect to ensure that sensitive information is properly handled before being used to build configurations.
	 * @see buildTenantConfigs for generating MSAL configurations based on the processed tenant data, which is called within this effect to create dynamic configurations that can be used for authentication with multiple tenants.
	 */
	useEffect(() => {
		processTenants()
	}, [tenantsData])

	/**
	 * Effect to initialize the MSAL instance and set the PCA in the Redux store based on dynamic tenant configurations.
	 * This effect runs when the component mounts and whenever the MSAL instance or dynamic configurations change. It checks if the MSAL instance is not already configured with a client ID, then it looks for the company identifier in local storage to find the corresponding tenant configuration. If a matching configuration is found, it initializes a new PublicClientApplication with that configuration and dispatches an action to update the PCA in the Redux store. If no matching configuration is found but there is already a PCA in the store, it uses that PCA instead. This allows for dynamic handling of multiple tenant configurations based on the user's company context.
	 * @see useMsal for accessing the MSAL instance and its configuration, which is used to determine if the PCA needs to be initialized or updated based on the dynamic tenant configurations.
	 */
	useEffect(() => {
		if (instance.getConfiguration().auth.clientId === '') {
			const company: string | null = localStorage.getItem('Company')
			if (company && dynamicConfigs[company]) {
				dispatch(
					changePca({
						pca: new PublicClientApplication(dynamicConfigs[company]),
					}),
				)
			} else if (appPca.pca) {
				// Use the PCA from the store if it's already configured
				dispatch(changePca({ pca: appPca.pca }))
			}
		}
	}, [instance, dynamicConfigs, appPca.pca, dispatch])

	/**
	 * Initializes the MSAL instance and acquires an access token silently.
	 * If silent acquisition fails due to interaction required, it falls back to interactive acquisition.
	 */
	useEffect(() => {
		instance.initialize().then(() => {
			const request = {
				scopes: ['user.read'],
				account: instance.getAllAccounts()[0],
				forceRefresh: false,
			}
			instance
				.acquireTokenSilent(request)
				.then(response => {
					dispatch(
						addAuth({
							auth: response.accessToken,
							LoginType: LoginType.backoffice,
							isLogged: true,
							loginResponse: null,
						}),
					)
				})
				.catch(async error => {
					if (error instanceof InteractionRequiredAuthError) {
						// fallback to interaction when silent call fails
						await instance.acquireTokenRedirect(request)
					}
				})
		})
	}, [instance])
	return (
		<div style={{ ...homeRootStyle } as React.CSSProperties}>
			{/* AppBar and existing menus */}
			<NavBar />
			<Box
				component={'main'}
				sx={homeMainBoxSx}
			>
				{/* Sidebar Drawer */}
				<SideBar />

				{/* Main content */}
				<Box sx={homeContentBoxSx}>
					{/* Central content */}
					<Box sx={homeCentralBoxSx}>
						<Grid
							xs={12}
							container
							spacing={2}
							sx={homeGridSx}
						>
							<Routes>
								<Route
									path='/'
									element={<Inbox />}
								/>
								<Route
									path='*'
									element={<Error />}
								/>
								<Route
									path='/profile'
									element={<Profile />}
								/>
								<Route
									path='/timesheet'
									element={<TimeSheetWithProvider />}
								/>
								<Route
									path='/timesheet/:id/print'
									element={<TimeSheetWithProvider />}
								/>
								<Route
									path='/timesheet/:id'
									element={<TimeSheetWithProvider />}
								/>
								<Route
									path='/timesheet/:id/print-service/:serviceId'
									element={<TimeSheetWithProvider />}
								/>
								<Route
									path='/schedules'
									element={<SchedulesPage />}
								/>
								<Route
									path='/schedules/service/:id'
									element={<SchedulesServicePage />}
								/>
								<Route
									path='/schedules/driver/:id'
									element={<SchedulesDriverPage />}
								/>
								<Route
									path='/schedules/vehicle/:id'
									element={<SchedulesVehiclePage />}
								/>
							</Routes>
						</Grid>
					</Box>
				</Box>
			</Box>
			<Alerts />
		</div>
	)
})

export default Home
